/**
 * workoutCalendar — Queries workouts for a given month and returns
 * a map of date string → { count, totalVolume, exercises }.
 * Caches results per userId/year/month.
 */
import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('WorkoutCalendar');

export interface DaySummary {
  count: number;
  totalVolume: number;
  exercises: string[];
}

export type MonthWorkoutMap = Record<string, DaySummary>;

// Simple in-memory cache keyed by "userId:year:month"
const cache = new Map<string, { data: MonthWorkoutMap; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export async function getMonthWorkouts(
  userId: string,
  year: number,
  month: number, // 0-indexed (JS Date convention)
): Promise<MonthWorkoutMap> {
  const cacheKey = `${userId}:${year}:${month}`;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.data;
  }

  // Build date range for the month
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

  const { data, error } = await supabase
    .from('workouts')
    .select('id, performed_at, payload')
    .eq('user_id', userId)
    .gte('performed_at', startDate.toISOString())
    .lte('performed_at', endDate.toISOString())
    .order('performed_at', { ascending: true });

  if (error) {
    log.error('Failed to fetch month workouts:', error);
    return {};
  }

  const map: MonthWorkoutMap = {};

  for (const workout of data ?? []) {
    // Use local date (not UTC) so workouts logged near midnight appear on the
    // correct calendar day in the user's timezone.
    const dateStr = workout.performed_at
      ? (() => {
          const d = new Date(workout.performed_at);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        })()
      : null;
    if (!dateStr) continue;

    if (!map[dateStr]) {
      map[dateStr] = { count: 0, totalVolume: 0, exercises: [] };
    }

    map[dateStr].count += 1;

    // Extract volume and exercises from payload
    const payload = workout.payload as any;
    if (payload?.exercises && Array.isArray(payload.exercises)) {
      for (const ex of payload.exercises) {
        const name = ex.name || 'Unknown';
        if (!map[dateStr].exercises.includes(name)) {
          map[dateStr].exercises.push(name);
        }
        if (Array.isArray(ex.sets)) {
          for (const set of ex.sets) {
            const weight = parseFloat(set.weight) || 0;
            const reps = parseInt(set.reps) || 0;
            map[dateStr].totalVolume += weight * reps;
          }
        }
      }
    }
  }

  cache.set(cacheKey, { data: map, ts: Date.now() });
  return map;
}

/** Invalidate cache for a specific month or all months */
export function invalidateCalendarCache(userId?: string, year?: number, month?: number) {
  if (userId && year !== undefined && month !== undefined) {
    cache.delete(`${userId}:${year}:${month}`);
  } else {
    cache.clear();
  }
}

/**
 * Calculate current workout streak (consecutive days with workouts ending today or yesterday).
 */
export function calculateStreak(monthData: MonthWorkoutMap, today: Date): number {
  // We need to walk backwards from today
  let streak = 0;
  const d = new Date(today);
  d.setHours(0, 0, 0, 0);

  // Check if today has a workout; if not, check yesterday as the start
  const todayStr = d.toISOString().split('T')[0];
  if (!monthData[todayStr]) {
    d.setDate(d.getDate() - 1);
    const yesterdayStr = d.toISOString().split('T')[0];
    if (!monthData[yesterdayStr]) return 0;
  }

  // Walk backwards counting consecutive days
  while (true) {
    const dateStr = d.toISOString().split('T')[0];
    if (monthData[dateStr]) {
      streak++;
      d.setDate(d.getDate() - 1);
    } else {
      break;
    }
  }

  return streak;
}
